import React, {useCallback, useRef} from 'react'

import CheckLineIcon from 'remixicon-react/CheckLineIcon'
import styled from 'styled-components'
import {palette} from 'styled-tools'

import {HierarchicalFacetOption} from '@festi/common/api/algolia'

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  count?: number
  children: React.ReactNode
  subFacets?: HierarchicalFacetOption[]
}

interface BoxProps {
  checked: boolean
}

const Item = styled.label`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  cursor: pointer;
  padding: 5px 0;
  user-select: none;
`

const Text = styled.span`
  font-size: 1rem;
  color: ${palette('blue')};
  line-height: 1.1;
  margin-left: 16px;
`

const Count = styled.span`
  color: ${palette('ui30Solid')};
  margin-left: 4px;
  transition: opacity 0.15s;
`

const Box = styled.div<BoxProps>`
  width: 16px;
  min-width: 16px;
  height: 16px;
  min-height: 16px;
  border: 1px solid ${palette('ui20Solid')};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  transition: background-color 0.15s, border-color 0.15s;

  .remixicon-icon {
    fill: transparent;
  }
`

const CheckboxInput = styled.input`
  position: absolute;
  border-radius: 0;
  opacity: 0;
  pointer-events: none;

  &:checked + ${Box} {
    background-color: ${palette('lightBlue')};
    border-color: ${palette('lightBlue')};

    .remixicon-icon {
      fill: ${palette('white')};
    }
  }

  &:focus + ${Box} {
    border-color: ${palette('lightBlue')};
  }
`

export default function FacetCheckboxItem({
  count,
  children,
  ...props
}: Props): JSX.Element {
  const filterRef = useRef<HTMLInputElement>()

  const onKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        filterRef.current?.click()
      }
    },
    [filterRef],
  )

  return (
    <Item>
      <Text>
        {children}
        <Count style={{opacity: count ? '1' : '0'}}>({count})</Count>
      </Text>

      <CheckboxInput
        type="checkbox"
        {...props}
        ref={filterRef}
        onKeyPress={onKeyPress}
      />

      <Box checked={props.checked}>
        <CheckLineIcon size={16} />
      </Box>
    </Item>
  )
}
